import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gql } from '@apollo/client';
import apolloClient from '../../graphql/apollo-client';
import { GET_INQUIRY, GET_INQUIRIES } from '../../graphql/queries';
import { useAuthStore } from '../../store/authStore';

const InquiryDetailPage: React.FC = () => {
  const { inquiryId } = useParams<{ inquiryId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { user } = useAuthStore();
  const [inquiry, setInquiry] = useState<any>(null);
  const [allInquiries, setAllInquiries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      if (!inquiryId || !user?.userId) return;
      setLoading(true);
      try {
        const [detailRes, listRes] = await Promise.all([
          apolloClient.query({ query: gql`${GET_INQUIRY}`, variables: { inquiryId }, fetchPolicy: 'network-only' }),
          apolloClient.query({ query: gql`${GET_INQUIRIES}`, variables: { userId: user.userId }, fetchPolicy: 'cache-first' }),
        ]);
        setInquiry(detailRes.data?.getInquiry || null);
        setAllInquiries(listRes.data?.getInquiries?.inquiries || []);
      } catch (err) { console.error('Failed to fetch inquiry:', err); }
      finally { setLoading(false); }
    };
    fetch();
  }, [inquiryId, user?.userId]);

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-6 text-center text-gray-400">{t('loading')}</div>;
  if (!inquiry) return <div className="max-w-4xl mx-auto px-4 py-6 text-center text-gray-400">{t('inquiry.notFound')}</div>;

  const currentIdx = allInquiries.findIndex((i: any) => i.inquiryId === inquiryId);
  const prevInq = currentIdx > 0 ? allInquiries[currentIdx - 1] : null;
  const nextInq = currentIdx < allInquiries.length - 1 ? allInquiries[currentIdx + 1] : null;

  const statusColor = inquiry.status === '답변완료' ? 'bg-green-100 text-green-700' : inquiry.status === '처리중' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600';

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <h1 className="text-xl font-bold text-gray-900 mb-2">{t('inquiry.title')}</h1>
      <hr className="border-gray-200 mb-6" />
      <div className="mb-4">
        <div className="flex items-center gap-3 mb-2">
          <span className={`px-2 py-0.5 text-xs font-medium rounded ${statusColor}`}>{inquiry.status}</span>
          <span className="text-xs text-gray-400">{inquiry.createdAt?.slice(0, 10)}</span>
        </div>
        <h2 className="text-lg font-bold text-gray-900">{inquiry.title}</h2>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 min-h-[200px]">
        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{inquiry.content}</p>
      </div>
      <hr className="border-gray-200 mb-4" />
      <div className="flex items-center justify-between">
        <button onClick={() => prevInq && navigate(`/inquiry/${prevInq.inquiryId}`)} disabled={!prevInq}
          className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
          {t('inquiry.prev')}
        </button>
        <button onClick={() => navigate('/inquiry')}
          className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
          {t('inquiry.list')}
        </button>
        <button onClick={() => nextInq && navigate(`/inquiry/${nextInq.inquiryId}`)} disabled={!nextInq}
          className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
          {t('inquiry.next')}
        </button>
      </div>
    </div>
  );
};

export default InquiryDetailPage;
